/**
 * Stakeholder-oriented architecture map: systems from meeting-derived model
 * plus actor "boundary" nodes, optional modules per system, and typed edges
 * (primary / manual / bidirectional / finance / feedback / dependency).
 *
 * The original `/architecture` diagram is unchanged — this dataset powers
 * `/architecture/map` for side-by-side comparison.
 */

import {
  architectureComponents,
  type ArchitectureComponent,
  DOMAIN_COLOR,
  DOMAIN_COLOR_SOFT,
  DOMAIN_LABEL,
  type Domain,
  STAGES,
  type Stage,
  STAGE_DESCRIPTION,
  STAGE_HEADLINE,
  STAGE_LABEL,
} from "@/presentation/data/architecture-components";

export { STAGES, STAGE_DESCRIPTION, STAGE_HEADLINE, STAGE_LABEL };
export type { Stage };

export type MapDomain = Domain | "actors";

export type ArchitectureMapNodeKind = "system" | "actor";

export interface ArchitectureMapNode {
  id: string;
  name: string;
  role: string;
  kind: ArchitectureMapNodeKind;
  ownership: "yandex" | "external";
  domain: MapDomain;
  stages: readonly Stage[];
  meetings: readonly string[];
  position: { x: number; y: number };
  note?: string;
  modules?: readonly string[];
}

export type ArchitectureMapEdgeKind =
  | "primary"
  | "manual"
  | "bidirectional"
  | "finance"
  | "feedback"
  | "dependency";

export interface ArchitectureMapEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  kind: ArchitectureMapEdgeKind;
}

/** Pan the whole system grid right/down so left column fits actors */
const MAP_SHIFT = { x: 480, y: 140 };

/**
 * Radial spread from a layout origin — pushes clusters apart (tune for density).
 * `GLOBAL_BLOWUP` applies an extra uniform scale after spread so wide cards do not overlap.
 */
const LAYOUT_ORIGIN = { x: 760, y: 440 };
const LAYOUT_SPREAD = 1.68;
const GLOBAL_BLOWUP = 1.14;

function shift(p: { x: number; y: number }) {
  return { x: p.x + MAP_SHIFT.x, y: p.y + MAP_SHIFT.y };
}

function spread(s: { x: number; y: number }) {
  return {
    x: LAYOUT_ORIGIN.x + (s.x - LAYOUT_ORIGIN.x) * LAYOUT_SPREAD,
    y: LAYOUT_ORIGIN.y + (s.y - LAYOUT_ORIGIN.y) * LAYOUT_SPREAD,
  };
}

function blowup(s: { x: number; y: number }) {
  return {
    x: LAYOUT_ORIGIN.x + (s.x - LAYOUT_ORIGIN.x) * GLOBAL_BLOWUP,
    y: LAYOUT_ORIGIN.y + (s.y - LAYOUT_ORIGIN.y) * GLOBAL_BLOWUP,
  };
}

/** Shift base grid, spread clusters, then slight global blow-up for card width */
function layoutNode(p: { x: number; y: number }) {
  return blowup(spread(shift(p)));
}

const SYSTEM_MODULES: Partial<Record<string, readonly string[]>> = {
  "yandex-pim": ["Catalog → products", "Bundles & connections", "Supplier sheets intake"],
  woody: ["ERP core", "Auto-Order (forecast → PO)", "Buy / sell price surface (with WMS)"],
  wms: [
    "Receiving & put-away",
    "Picker path + scanner",
    "Dispatch live board",
    "Shifts / All Slots (imports)",
    "Shortage → Woody (lagged)",
    "Chatterbox from scanner",
  ],
  "adminca-promotions": ["Base catalog price", "Promotion layer", "Ties to discounts engine"],
  "adminca-coupons": ["Cart-level JSON coupons"],
  "adminca-orders": ["Orders view", "Refunds button → EZCount", "Courier shift lookup → WMS"],
  "comm-system": ["Admin Panel (content)", "Adminca Experiments (audiences)"],
  "yandex-pro": ["Shifts visible to driver", "Order assignment from dispatch"],
  chatterbox: ["Suggest → ML → agent", "Per-line routing"],
  "woody-autoorder": ["3-week weighted forecast", "Manual approval before send"],
};

function fromBase(c: ArchitectureComponent): ArchitectureMapNode {
  return {
    id: c.id,
    name: c.name,
    role: c.role,
    kind: "system",
    ownership: c.ownership,
    domain: c.domain,
    stages: c.stages,
    meetings: c.meetings,
    position: layoutNode(c.position),
    note: c.note,
    modules: SYSTEM_MODULES[c.id],
  };
}

function findBase(id: string): ArchitectureComponent {
  const c = architectureComponents.find((x) => x.id === id);
  if (!c) throw new Error(`Missing base component ${id}`);
  return c;
}

const extraSystems: ArchitectureMapNode[] = (() => {
  const chatter = layoutNode(findBase("chatterbox").position);
  const finance0 = layoutNode(findBase("ezcount").position);
  const wms = layoutNode(findBase("wms").position);
  const yandexPro = layoutNode(findBase("yandex-pro").position);
  const nirvana = layoutNode(findBase("nirvana").position);
  const datalens = layoutNode(findBase("datalens").position);
  // Suppress unused-binding warning if no extras anchor on yandexPro
  void yandexPro;
  return [
    {
      id: "adminca-orders",
      name: "Adminca · Orders",
      role: "Order-centric agent console — refunds, notes, WMS shift queries",
      kind: "system",
      ownership: "yandex",
      domain: "customer-experience",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-06-customer-support-discovery"],
      position: { x: chatter.x - 480, y: chatter.y + 20 },
      modules: SYSTEM_MODULES["adminca-orders"],
    },
    {
      id: "itz-payment-engine",
      name: "Payment calculation engine",
      role: "Itz-built engine — WMS exports + bonus CSV → calculated payouts",
      kind: "system",
      ownership: "external",
      domain: "finance",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-11-delivery-operations-walkthrough"],
      position: { x: wms.x + 420, y: wms.y - 120 },
    },
    {
      id: "bank-settlement",
      name: "Bank",
      role: "Card refund settlement after EZCount (support walkthrough)",
      kind: "system",
      ownership: "external",
      domain: "finance",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-06-customer-support-discovery"],
      position: { x: finance0.x - 380, y: finance0.y + 240 },
    },
    {
      id: "leonid-dashboard",
      name: "Leonid dashboard",
      role: "Historical orders analytics feeding weekly shift planning",
      kind: "system",
      ownership: "external",
      domain: "data-bi",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-11-delivery-operations-walkthrough"],
      position: { x: nirvana.x - 100, y: nirvana.y - 220 },
    },
    {
      id: "koko-push",
      name: "Koko",
      role: "Push / SMS marketing to drivers (delivery ops comms subgraph)",
      kind: "system",
      ownership: "yandex",
      domain: "customer-experience",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-11-delivery-operations-walkthrough"],
      position: { x: yandexPro.x - 420, y: yandexPro.y - 180 },
    },
    {
      id: "yql-warehouse",
      name: "YQL / warehouse",
      role: "Query + warehouse layer between Nirvana and Datalens (BI walkthrough)",
      kind: "system",
      ownership: "yandex",
      domain: "data-bi",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-11-bi-analytics-walkthrough"],
      position: {
        x: (nirvana.x + datalens.x) / 2,
        y: datalens.y - 140,
      },
    },
    {
      id: "eivos-wms-dev",
      name: "Eivos (WMS dev)",
      role: "External Yandex dev team — all WMS changes queue here (risk, not a data product)",
      kind: "system",
      ownership: "external",
      domain: "fulfillment-ops",
      stages: ["b2c", "b2b"],
      meetings: ["2026-05-12-wms-operations-walkthrough"],
      position: { x: wms.x + 420, y: wms.y + 240 },
      note: "Organizational dependency — edges use the dependency style",
    },
  ];
})();

const actorNodes: ArchitectureMapNode[] = [
  {
    id: "actor-suppliers",
    name: "Suppliers",
    role: "Receive POs · email / WhatsApp / EDI",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-05-b2b-supply-chain-discovery"],
    position: { x: -60, y: 48 },
  },
  {
    id: "actor-commercial-ops",
    name: "Commercial & category ops",
    role: "Owns supplier sheets, price mapa, manual loads into Woody",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2c", "b2b", "mvp"],
    meetings: [
      "2026-05-05-b2b-supply-chain-discovery",
      "2026-05-11-pricing-promotions-walkthrough",
    ],
    position: { x: -60, y: 420 },
  },
  {
    id: "actor-store-ops",
    name: "Dark-store ops & pickers",
    role: "Scanner-driven picking, receiving, cycle counts in WMS",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2c", "b2b", "mvp"],
    meetings: ["2026-05-12-wms-operations-walkthrough"],
    position: { x: -60, y: 820 },
  },
  {
    id: "actor-support-agents",
    name: "Support agents",
    role: "Chatterbox lines → Adminca orders, refunds, Tracker forms",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-06-customer-support-discovery"],
    position: { x: 3180, y: 520 },
  },
  {
    id: "actor-courier",
    name: "Couriers",
    role: "Yandex Pro — shifts, payouts, delivery execution",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2c", "b2b"],
    meetings: ["2026-05-11-delivery-operations-walkthrough"],
    position: { x: 3180, y: 900 },
  },
  {
    id: "actor-b2c-shopper",
    name: "B2C shopper",
    role: "End customer — app, chat, payment, delivery tracking",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2c"],
    meetings: [
      "2026-05-06-customer-support-discovery",
      "2026-05-11-pricing-promotions-walkthrough",
    ],
    position: { x: 3180, y: 80 },
  },
  {
    id: "actor-b2b-buyer",
    name: "B2B buyer org",
    role: "Business client — invoicing / Obligo-style flows (where applicable)",
    kind: "actor",
    ownership: "external",
    domain: "actors",
    stages: ["b2b", "mvp"],
    meetings: ["2026-05-05-b2b-supply-chain-discovery", "2026-05-06-customer-support-discovery"],
    position: { x: 3180, y: 300 },
  },
];

/**
 * First-load seed for the editable shared architecture map workspace.
 * Persisted as `architecture_workspace_state.map_nodes` once the user opens
 * the map; thereafter the workspace is the source of truth. Edit this seed
 * only to update the default state for a fresh workspace (or before any user
 * has dragged a node).
 */
export const MAP_SEED_NODES: readonly ArchitectureMapNode[] = [
  ...architectureComponents.map(fromBase),
  ...extraSystems,
  ...actorNodes,
];

/**
 * Set of architecture node ids that a feature can be bound to via
 * `features.component_id`. Only system nodes — actors never get features.
 * Used by the app layer to validate `component_id` (no DB FK).
 *
 * Derived from the seed; the runtime workspace may add nodes the validator
 * doesn't know about. That's OK — the validator is advisory.
 */
export const KNOWN_COMPONENT_IDS: ReadonlySet<string> = new Set(
  MAP_SEED_NODES.filter((n) => n.kind === "system").map((n) => n.id)
);

/** Look up a system node by id; returns null for unknown or actor ids. */
export function getArchitectureNodeById(id: string | null | undefined): ArchitectureMapNode | null {
  if (!id) return null;
  const node = MAP_SEED_NODES.find((n) => n.id === id);
  return node && node.kind === "system" ? node : null;
}

export const MAP_DOMAIN_LABEL: Record<MapDomain, string> = {
  ...DOMAIN_LABEL,
  actors: "People & boundaries",
};

export const MAP_DOMAIN_COLOR: Record<MapDomain, string> = {
  ...DOMAIN_COLOR,
  actors: "oklch(0.55 0.09 35)",
};

export const MAP_DOMAIN_COLOR_SOFT: Record<MapDomain, string> = {
  ...DOMAIN_COLOR_SOFT,
  actors: "oklch(0.93 0.04 40)",
};

/** @internal */
function baseEdges(): ArchitectureMapEdge[] {
  return [
    {
      id: "e-gs1-pim",
      source: "gs1",
      target: "yandex-pim",
      label: "manual lookup",
      kind: "primary",
    },
    { id: "e-merlog-pim", source: "merlog", target: "yandex-pim", kind: "primary" },
    {
      id: "e-sheets-pim",
      source: "manual-spreadsheets",
      target: "yandex-pim",
      label: "onboarding",
      kind: "manual",
    },
    { id: "e-pim-tanker", source: "yandex-pim", target: "tanker-key", kind: "primary" },
    { id: "e-pim-discounts", source: "yandex-pim", target: "discounts-engine", kind: "primary" },
    {
      id: "e-discounts-promos",
      source: "discounts-engine",
      target: "adminca-promotions",
      kind: "primary",
    },
    {
      id: "e-promos-coupons",
      source: "adminca-promotions",
      target: "adminca-coupons",
      kind: "primary",
    },
    {
      id: "e-sheets-woody",
      source: "manual-spreadsheets",
      target: "woody",
      label: "price mapa",
      kind: "manual",
    },
    { id: "e-autoorder-woody", source: "woody-autoorder", target: "woody", kind: "primary" },
    {
      id: "e-woody-wms",
      source: "woody",
      target: "wms",
      label: "inbound / tasks",
      kind: "primary",
    },
    {
      id: "e-wms-woody-shortage",
      source: "wms",
      target: "woody",
      label: "shortage → PO (lagged)",
      kind: "feedback",
    },
    {
      id: "e-wms-pim",
      source: "wms",
      target: "yandex-pim",
      label: "inventory / catalog sync",
      kind: "feedback",
    },
    {
      id: "e-sheets-wms-shifts",
      source: "manual-spreadsheets",
      target: "wms",
      label: "shift plan → All Slots",
      kind: "manual",
    },
    {
      id: "e-shiftorg-wms-parallel",
      source: "shift-organizer",
      target: "wms",
      label: "not integrated — parallel planning",
      kind: "manual",
    },
    {
      id: "e-crmgo-yandexpro",
      source: "crm-go",
      target: "yandex-pro",
      label: "onboarding",
      kind: "primary",
    },
    {
      id: "e-wms-yandexpro",
      source: "wms",
      target: "yandex-pro",
      label: "dispatch → driver",
      kind: "primary",
    },
    {
      id: "e-yandexpro-wms",
      source: "yandex-pro",
      target: "wms",
      label: "status / execution",
      kind: "bidirectional",
    },
    { id: "e-promos-app", source: "adminca-promotions", target: "yango-app", kind: "primary" },
    { id: "e-coupons-app", source: "adminca-coupons", target: "yango-app", kind: "primary" },
    { id: "e-comm-app", source: "comm-system", target: "yango-app", kind: "primary" },
    { id: "e-hyp-app", source: "yad", target: "yango-app", label: "payment", kind: "primary" },
    {
      id: "e-yandexpro-app",
      source: "yandex-pro",
      target: "yango-app",
      label: "delivery",
      kind: "primary",
    },
    {
      id: "e-app-appmetrica",
      source: "yango-app",
      target: "appmetrica",
      label: "events",
      kind: "primary",
    },
    { id: "e-appmetrica-datalens", source: "appmetrica", target: "datalens", kind: "primary" },
    {
      id: "e-nirvana-yql",
      source: "nirvana",
      target: "yql-warehouse",
      label: "scheduled SQL",
      kind: "primary",
    },
    { id: "e-yql-datalens", source: "yql-warehouse", target: "datalens", kind: "primary" },
    { id: "e-nirvana-seg", source: "nirvana", target: "segmentation-service", kind: "primary" },
    { id: "e-seg-sms", source: "segmentation-service", target: "sms-gateway", kind: "primary" },
    { id: "e-seg-comm", source: "segmentation-service", target: "comm-system", kind: "primary" },
    {
      id: "e-chatter-adminca",
      source: "chatterbox",
      target: "adminca-orders",
      label: "agent tools",
      kind: "primary",
    },
    {
      id: "e-adminca-ezcount",
      source: "adminca-orders",
      target: "ezcount",
      label: "refunds",
      kind: "finance",
    },
    {
      id: "e-adminca-wms",
      source: "adminca-orders",
      target: "wms",
      label: "courier shift lookup",
      kind: "manual",
    },
    { id: "e-chatter-tracker", source: "chatterbox", target: "tracker", kind: "primary" },
    {
      id: "e-ezcount-bank",
      source: "ezcount",
      target: "bank-settlement",
      label: "settlement",
      kind: "finance",
    },
    { id: "e-obligo-ezcount", source: "obligo", target: "ezcount", kind: "finance" },
    {
      id: "e-wms-itz",
      source: "wms",
      target: "itz-payment-engine",
      label: "payment export + bonus CSV",
      kind: "finance",
    },
    { id: "e-itz-priority", source: "itz-payment-engine", target: "priority", kind: "finance" },
    {
      id: "e-priority-cabinet",
      source: "priority",
      target: "driver-cabinet",
      kind: "finance",
    },
    {
      id: "e-koko-yandexpro",
      source: "koko-push",
      target: "yandex-pro",
      label: "push / SMS",
      kind: "primary",
    },
    {
      id: "e-leonid-sheets",
      source: "leonid-dashboard",
      target: "manual-spreadsheets",
      label: "shift planning input",
      kind: "manual",
    },
    {
      id: "e-wms-chatter",
      source: "wms",
      target: "chatterbox",
      label: "order events / missing items",
      kind: "primary",
    },
    { id: "e-wms-eivos", source: "wms", target: "eivos-wms-dev", kind: "dependency" },
    {
      id: "e-actor-suppliers-woody",
      source: "actor-suppliers",
      target: "woody",
      label: "PO / delivery",
      kind: "primary",
    },
    {
      id: "e-actor-suppliers-merlog",
      source: "actor-suppliers",
      target: "merlog",
      kind: "primary",
    },
    {
      id: "e-actor-commercial-sheets",
      source: "actor-commercial-ops",
      target: "manual-spreadsheets",
      kind: "manual",
    },
    { id: "e-actor-store-wms", source: "actor-store-ops", target: "wms", kind: "primary" },
    { id: "e-actor-b2c-app", source: "actor-b2c-shopper", target: "yango-app", kind: "primary" },
    {
      id: "e-actor-b2c-chatter",
      source: "actor-b2c-shopper",
      target: "chatterbox",
      label: "support entry",
      kind: "primary",
    },
    { id: "e-actor-b2b-app", source: "actor-b2b-buyer", target: "yango-app", kind: "primary" },
    {
      id: "e-actor-b2b-obligo",
      source: "actor-b2b-buyer",
      target: "obligo",
      label: "credit / billing",
      kind: "finance",
    },
    {
      id: "e-actor-support-chatter",
      source: "actor-support-agents",
      target: "chatterbox",
      kind: "primary",
    },
    {
      id: "e-actor-support-adminca",
      source: "actor-support-agents",
      target: "adminca-orders",
      kind: "primary",
    },
    { id: "e-actor-courier-pro", source: "actor-courier", target: "yandex-pro", kind: "primary" },
  ];
}

/**
 * First-load seed for the editable shared architecture map workspace.
 * Persisted as `architecture_workspace_state.map_edges` once the user opens
 * the map; thereafter the workspace is the source of truth.
 */
export const MAP_SEED_EDGES: readonly ArchitectureMapEdge[] = baseEdges();
